import type ExcelJS from 'exceljs';

/**
 * The label list (bos-app#232, AC-07): the barcodes BOS generated, one row
 * each, for the owner's label printer. The catalog import's Result and the
 * Products page's Barcode issues tab both build it from `generatedBarcodes`.
 *
 * exceljs is loaded through a dynamic `import()`, as in the products export,
 * so its chunk is fetched only when someone downloads. Pure of React and
 * i18next: the caller passes the copy, and tests read the workbook back.
 */

/** One generated barcode; `GeneratedBarcode` from the API fits it as is. */
export type LabelListRow = {
    productName: string;
    /** `null` for a variant without options (a simple product). */
    variantLabel: string | null;
    /** `null` when the variant has no SKU. */
    sku: string | null;
    barcode: string;
    /** The labels to print: the units in stock. */
    units: number;
};

export type LabelListColumnKey =
    'product' | 'variant' | 'sku' | 'barcode' | 'labels';

/** The sheet's name and column headers, in the reader's language. */
export type LabelListLabels = {
    sheet: string;
    columns: Record<LabelListColumnKey, string>;
};

export const LABEL_LIST_COLUMNS: readonly {
    key: LabelListColumnKey;
    width: number;
}[] = [
    { key: 'product', width: 40 },
    { key: 'variant', width: 28 },
    { key: 'sku', width: 32 },
    { key: 'barcode', width: 18 },
    { key: 'labels', width: 10 },
];

type CellValue = string | number | null;

/**
 * One row's cells. A missing variant or SKU is an empty cell, never the text
 * "null"; the barcode stays text so a leading zero survives.
 */
export const labelListCells = (
    row: LabelListRow,
): Record<LabelListColumnKey, CellValue> => ({
    product: row.productName,
    variant: row.variantLabel,
    sku: row.sku,
    barcode: row.barcode,
    labels: row.units,
});

/**
 * The workbook: one sheet, a bold frozen header row, then one row per
 * generated barcode in the order given.
 */
export const buildLabelListWorkbook = async (
    rows: readonly LabelListRow[],
    labels: LabelListLabels,
): Promise<ExcelJS.Workbook> => {
    const { Workbook } = (await import('exceljs')).default;
    const workbook = new Workbook();

    const sheet = workbook.addWorksheet(labels.sheet, {
        views: [{ state: 'frozen', ySplit: 1 }],
    });
    sheet.columns = LABEL_LIST_COLUMNS.map((column) => ({
        key: column.key,
        header: labels.columns[column.key],
        width: column.width,
    }));
    sheet.getRow(1).font = { bold: true };
    rows.forEach((row) => {
        sheet.addRow(labelListCells(row));
    });

    return workbook;
};
